import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ErrorGenericSchema } from '../../models';

extendZodWithOpenApi(z);
const c = initContract();

export const TimeEntryEventSchema = z.object({
  event_id: z.number().int().min(1).openapi({
    description: 'Unique event ID from Timely (auto-increment)',
    example: 12345,
  }),
  event_type: z.enum(['sync', 'delete']).openapi({
    description: '"sync" ensures external entry exists and matches; "delete" ensures removal',
    example: 'sync',
  }),
  account_id: z.number().int().min(1).openapi({
    description: 'Timely account ID',
    example: 100,
  }),
  user_id: z.number().int().min(1).openapi({
    description: 'Timely user ID',
    example: 200,
  }),
  hour_id: z.number().int().min(1).openapi({
    description: 'Timely hour (time entry) ID',
    example: 300,
  }),
  tic_task_uri: z.string().max(255).openapi({
    description: 'TIC resource URI for the task',
    example: 'tic://jira/workspace-123/tasks/PROJ-456',
  }),
  tool_id: z.string().max(50).openapi({
    description: 'Integration tool identifier',
    example: 'jira',
  }),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .openapi({
      description: 'Date of the time entry (YYYY-MM-DD)',
      example: '2024-01-15',
    }),
  start_time: z.string().nullable().openapi({
    description: 'ISO 8601 datetime with timezone, or null if hour has no timestamps',
    example: '2024-01-15T09:00:00+01:00',
  }),
  user_timezone: z.string().max(50).openapi({
    description: "User's timezone (IANA format)",
    example: 'Europe/London',
  }),
  logged_seconds: z.number().int().nonnegative().openapi({
    description: 'Duration in seconds',
    example: 3600,
  }),
  description: z.string().max(1024).nullable().openapi({
    description: 'Time entry description (truncated to 1024 chars)',
    example: 'Working on feature X',
  }),
});

export type TimeEntryEvent = z.infer<typeof TimeEntryEventSchema>;

const TimeEntryEventsRequestSchema = z.object({
  events: z.array(TimeEntryEventSchema).min(1).max(5000).openapi({
    description: 'Array of time entry events (typically batches of 300)',
  }),
});

const TimeEntryEventsResponseSchema = z.object({
  processed: z.number().int().openapi({
    description: 'Number of events received',
    example: 2,
  }),
  message: z.string().optional().openapi({
    description: 'Additional information about processing',
    example: 'Received 2 events: 2 inserted, 0 duplicates ignored',
  }),
});

export const apiTimelyTimeEntryEventsRoute = c.router({
  receiveEvents: {
    method: 'POST',
    path: `/1.0/timely/time_entry_events`,
    headers: z.object({
      authorization: z.string().openapi({
        description: 'Bearer token using INTERNAL_TIMELY_WEBHOOK_API_KEY',
        example: 'Bearer secret-key',
      }),
    }),
    body: TimeEntryEventsRequestSchema,
    responses: {
      200: TimeEntryEventsResponseSchema,
      400: ErrorGenericSchema,
      401: ErrorGenericSchema,
      500: ErrorGenericSchema,
    },
    summary: 'Receive time entry sync events from Timely',
    description: `Webhook endpoint for Timely to push time entry sync events.

Events are processed with idempotency via \`event_id\` as primary key.
Duplicate events (same \`event_id\`) are ignored with ON CONFLICT DO NOTHING.

Authentication requires the \`INTERNAL_TIMELY_WEBHOOK_API_KEY\` as a Bearer token.`,
  },
});
